import React, { useEffect, useState } from 'react'

import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import moment from 'moment-timezone'
import { useStores } from 'presentation/hooks/use-stores'
import * as yup from 'yup'

import Container from 'presentation/hospital/components/Container'
import OverviewCard from 'presentation/hospital/components/DoctorRegister/Details/OverviewCard'
import ScrollableTabs from 'presentation/shared/components/Tabs/ScrollableTabs'
import Recommendation from 'presentation/hospital/components/DoctorRegister/Details/Recommendation'
import PersonalData from 'presentation/hospital/components/DoctorRegister/Details/PersonalData'
import ProfessionalData from 'presentation/hospital/components/DoctorRegister/Details/ProfessionalData'
import ActingSectors from 'presentation/hospital/components/DoctorRegister/Details/ActingSectors'
import Attendances from 'presentation/hospital/components/DoctorRegister/Details/Attendances'
import Immunizations from 'presentation/hospital/components/DoctorRegister/Details/Immunizations'
import Documents from 'presentation/hospital/components/DoctorRegister/Details/Documents'
import SourceValidation from 'presentation/hospital/components/DoctorRegister/Details/SourceValidation'
import ActualPage from 'presentation/shared/components/ActualPage'
import Button from 'presentation/shared/components/Button'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { useServices } from 'presentation/hooks/use-services'
import { Hospital } from 'domain/entities/hospital-model'
import { actingSectorsOptions } from 'presentation/doctor/components/Forms/AddDoctorInfo/ActingSectors'

import * as S from './styles'
import { DoctorRegistrationSteps } from 'common/enum/doctor-overall-registration-steps'
import CreateRegisterPendencyModal from '../modals/CreateRegisterPendency/createRegisterPendency'
import { LoadDoctorInfo } from 'domain/usecases/doctor/load-doctor-info'
import { academicFormationsOptions } from 'presentation/utils/translators/academic-formation'
import { newRegistrationDetailsContext } from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails/context'
import {
  DoctorRegister,
  DoctorRegisterStatus,
  DoctorRegisterStepStatus
} from 'domain/entities/doctor-register'
import { observer } from 'mobx-react'
import { isEqual } from 'lodash'
import ConfirmationModal from 'presentation/shared/components/Modal'
import DoctorConfirmation from 'presentation/assets/icons/doctor-confirmation.png'
import DoctorReview from 'presentation/assets/icons/doctor-review.svg'
import ReproveRegisterImage from 'presentation/assets/icons/reprove-register.svg'
import Heading from 'presentation/shared/components/Heading'
import SuccessRegisterPendency from 'presentation/assets/icons/success-register-pendency.png'
import SuccessModal from 'presentation/shared/components/Modal'
import { Doctor } from 'domain/entities/doctor-model'

const NewRegistrationDetailsLayout = WithLoading(
  observer(({ setIsLoading }: WithLoadingProps) => {
    const [doctorDetails, setDoctorDetails] = useState<LoadDoctorInfo.Model>()
    const [hospitalUnits, setHospitalUnits] = useState<Hospital[]>([])
    const [sendToDirectorModal, setSendToDirectorModal] =
      useState<boolean>(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showPendencyModal, setShowPendencyModal] = useState(false)
    const [showReviewModal, setShowReviewModal] = useState(false)
    const [showApprovedModal, setShowApprovedModal] = useState(false)
    const [showReprovedModal, setShowReprovedModal] = useState(false)
    const history = useHistory()
    const { doctorRegister } = useServices()
    const [toggleInfo, setToggleInfo] = useState(false)
    const { state } = useLocation<{
      doctor_id: number
    }>()

    const isDirector = newRegistrationDetailsContext.isDirector

    const doctorService = useServices().doctor
    const doctorRegisterStepService = useServices().doctorRegisterStep
    const currentHospital = useStores().currentHospital

    const getFormattedUnits = (): string[] | undefined => {
      if (hospitalUnits.length > 0) {
        const selectedHospitals = []
        for (const hospital of hospitalUnits) {
          if (hospital) selectedHospitals.push(`Mater Dei ${hospital?.name}`)
        }
        return selectedHospitals
      }
    }

    const getMainCouncilRegister = () => {
      const { councilRegister } = doctorDetails ?? {}

      if (!councilRegister || councilRegister.length === 0) {
        return
      }

      return councilRegister.find(({ isMain }) => isMain) ?? councilRegister[0]
    }

    const getSecondarySpecialties = () => {
      if (!doctorDetails?.specialties) {
        return
      }
      return doctorDetails.specialties.filter((specialty) => !specialty.isMain)
    }
    const getParsedSecondarySpecialities = () => {
      const secondarySpecialities = getSecondarySpecialties() || []

      return secondarySpecialities.map((item) => item.name)
    }
    const getMainSpeciality = (doctor?: Doctor) => {
      if (!doctor?.specialties) {
        return
      }

      return doctor?.specialties?.find((item) => item.isMain)
    }

    const getActingSectorsDescriptions = () => {
      if (!doctorDetails?.sectors) {
        return
      }

      return doctorDetails?.sectors.map((actingSector) => {
        const actingSectorOption = actingSectorsOptions.find((option) => {
          return option.value === actingSector
        })

        if (actingSectorOption) {
          return actingSectorOption.label
        }

        return ''
      })
    }

    const getAcademicFormationDescription = () => {
      if (!doctorDetails?.academicFormation) {
        return ''
      }
      const academicFormation = academicFormationsOptions.find(
        (option) => option.value === doctorDetails?.academicFormation
      )
      if (academicFormation) {
        return academicFormation.label
      }
      return ''
    }

    const findDescriptionAndStatusForStep = (step: DoctorRegistrationSteps) => {
      const foundRegister = newRegistrationDetailsContext.register?.steps?.find(
        (x) => {
          return x.name === step
        }
      )

      return {
        [step]: {
          description:
            foundRegister?.status === DoctorRegisterStepStatus.PENDENT
              ? foundRegister?.getActivePendency?.text
              : '',
          director_review:
            foundRegister?.status === DoctorRegisterStepStatus.REVIEW
              ? foundRegister?.getActiveReview?.text
              : '',
          status: foundRegister?.status
        }
      } as PendencyInitialValues
    }

    function getStepPendencyValues(...steps: DoctorRegistrationSteps[]) {
      return steps
        .map((step) => {
          return findDescriptionAndStatusForStep(step)
        })
        .reduce((acc, curr) => {
          return { ...acc, ...curr }
        }, {} as PendencyInitialValues)
    }

    const fetchDoctorWithRecommendation = async () => {
      try {
        setIsLoading(true)
        if (state?.doctor_id) {
          const doctorWithRecommendation = await doctorService.loadDoctorInfo(
            state.doctor_id,
            [
              'name',
              'phone',
              'email',
              'rg',
              'cpf',
              'birthDate',
              'gender',
              'birthInfo {state_id, stateName, city_id, cityName}',
              'ethnicity',
              'motherName',
              'fatherName',
              'badgeName',
              'emergencyPhone',
              'emergencyContact',
              'materDeiStudent',
              'modality',
              'startCollegeDate',
              'endCollegePrediction',
              'academicFormation',
              'getDoctorRecommendation { doctor_recommendation_id, recommended_by_id, units, emergency, emergencyJustification, reasonEmergency, createdAt, updatedAt, specialty {code, name}, recommendedByDoctor { doctor_id, name, specialty, specialties { name, isMain } } }',
              'councilRegister { issueDate, isMain, crm, crmUf }',
              'academicInstitution',
              'lattes',
              'linkedin',
              'sectors',
              'otherClinics',
              'privateOffice',
              'medicalInsurance',
              'medicalTeam { description }',
              'specialties {name, isMain}',
              'specialty',
              'immunization {description, immunized}',
              'privileges',
              'modality',
              'createdAt',
              'createdInMvAt',
              'otherClinicsName',
              'address {zipCode, district, city, neighborhood, street, number, complement}',
              'doctorRegister { ' +
                'doctor_register_id, ' +
                'doctor_id, ' +
                'sentToHospitalAt, ' +
                'status, steps { doctor_register_step_id, name, status, getActivePendency {text}, getActiveReview {text}}, ' +
                'documentValidations { doctor_document_validation_id, title, description, document { document_id, name, type, updatedAt } } }',
              'doctorDocuments { document_id, name, documentUri, type, updatedAt, conclusionDate }'
            ]
          )

          Promise.all([
            newRegistrationDetailsContext.addRegister(
              doctorWithRecommendation.doctorRegister
            ),
            setDoctorDetails(doctorWithRecommendation)
          ])
        }
      } catch (err: any) {
        toast.error(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    // const mainSpecialty = doctorDetails?.specialties?.find(
    //   (specialty) => specialty.isMain === true
    // )?.name
    const tabItems = [
      {
        label: 'Indicação',
        componentChildren: (
          <Recommendation
            setToggleInfo={setToggleInfo}
            toggleInfo={toggleInfo}
            recommendation={{
              recommended_doctor_id: doctorDetails?.doctorRegister?.doctor_id,
              doctor_recommendation_id:
                doctorDetails?.getDoctorRecommendation
                  ?.doctor_recommendation_id,
              hospitalUnits: doctorDetails?.getDoctorRecommendation?.units,
              recommendedBy: {
                doctorName:
                  doctorDetails?.getDoctorRecommendation?.recommendedByDoctor
                    ?.name,
                doctorId:
                  doctorDetails?.getDoctorRecommendation?.recommended_by_id,
                doctorSpecialty:
                  doctorDetails?.getDoctorRecommendation?.recommendedByDoctor
                    ?.specialty
              },

              recommendedDate: doctorDetails?.getDoctorRecommendation?.updatedAt
                ? doctorDetails?.getDoctorRecommendation?.updatedAt
                    .toLocaleString()
                    .substring(0, 10)
                : doctorDetails?.getDoctorRecommendation?.createdAt
                    .toLocaleString()
                    .substring(0, 10),
              specialty: doctorDetails?.getDoctorRecommendation?.specialty.name,
              isEmergency: doctorDetails?.getDoctorRecommendation?.emergency,
              reasonEmergency:
                doctorDetails?.getDoctorRecommendation
                  ?.translatedEmergencyReason ||
                doctorDetails?.getDoctorRecommendation?.reasonEmergency,
              justification:
                doctorDetails?.getDoctorRecommendation?.emergencyJustification
            }}
            units={getFormattedUnits()}
          />
        )
      },
      {
        label: 'Dados pessoais',
        componentChildren: (
          <PersonalData
            personalData={{
              name: doctorDetails?.name,
              birthDay: moment(doctorDetails?.birthDate).format('DD/MM/YYYY'),
              gender: doctorDetails?.gender,
              identityCard: doctorDetails?.rg,
              cpf: doctorDetails?.cpf,
              birthInfo: doctorDetails?.birthInfo,
              ethnicity: doctorDetails?.ethnicity,
              motherName: doctorDetails?.motherName,
              fatherName: doctorDetails?.fatherName,
              badgeName: doctorDetails?.badgeName
            }}
            contacts={{
              email: doctorDetails?.email,
              phone: doctorDetails?.phone,
              emergencyContact: doctorDetails?.emergencyContact,
              emergencyPhone: doctorDetails?.emergencyPhone
            }}
            address={{
              city: doctorDetails?.address?.city,
              zipCode: doctorDetails?.address?.zipCode,
              district: doctorDetails?.address?.district,
              neighborhood: doctorDetails?.address?.neighborhood,
              number: doctorDetails?.address?.number,
              complement: doctorDetails?.address?.complement,
              homeAddress: doctorDetails?.address?.street
            }}
            registrationStatus={getStepPendencyValues(
              DoctorRegistrationSteps.PERSONAL_DATA,
              DoctorRegistrationSteps.ADDRESS,
              DoctorRegistrationSteps.CONTACTS
            )}
            updateStepStatus={updateStepStatus}
            isDirector={isDirector}
          />
        )
      },
      {
        label: 'Dados profissionais',
        componentChildren: (
          <ProfessionalData
            professionalData={{
              isStudentDoctor: doctorDetails?.materDeiStudent,
              modality: doctorDetails?.modality,
              startCollegeDate: doctorDetails?.startCollegeDate
                ? moment(doctorDetails?.startCollegeDate).format('DD/MM/YYYY')
                : undefined,
              endCollegePrediction: doctorDetails?.endCollegePrediction
                ? moment(doctorDetails?.endCollegePrediction).format(
                    'DD/MM/YYYY'
                  )
                : undefined,
              academicFormation: doctorDetails?.academicFormation,
              academicInstitution: doctorDetails?.academicInstitution,
              specialty: doctorDetails?.mainSpecialty?.name,
              lattes: doctorDetails?.lattes,
              linkedin: doctorDetails?.linkedin,
              councilRegister: getMainCouncilRegister(),
              secondarySpecialty: getSecondarySpecialties()
            }}
            initialValues={getStepPendencyValues(
              DoctorRegistrationSteps.ACADEMIC_EDUCATION,
              DoctorRegistrationSteps.PROFESSIONAL_DATA,
              DoctorRegistrationSteps.SPECIALTIES,
              DoctorRegistrationSteps.TEAMS
            )}
            updateStepStatus={updateStepStatus}
            teams={doctorDetails?.medicalTeam}
            isDirector={isDirector}
          />
        )
      },
      {
        label: 'Setores de atuação',
        hide: isDirector,
        componentChildren: (
          <ActingSectors
            actingSectors={getActingSectorsDescriptions()}
            teams={doctorDetails?.medicalTeam}
            initialValues={getStepPendencyValues(
              DoctorRegistrationSteps.SECTORS_OF_ACTIVITY,
              DoctorRegistrationSteps.TEAMS
            )}
            updateStepStatus={updateStepStatus}
          />
        )
      },
      {
        label: 'Atendimentos',
        hide: isDirector,
        componentChildren: (
          <Attendances
            attendances={{
              otherClinics: doctorDetails?.otherClinics,
              otherClinicsName: doctorDetails?.otherClinicsName,
              hasPrivateClinic: doctorDetails?.privateOffice,
              hasExternalHealthInsurance:
                doctorDetails?.medicalInsurance &&
                doctorDetails?.medicalInsurance.length > 0,
              externalHealthInsurances: doctorDetails?.medicalInsurance,
              medicalInsurance: doctorDetails?.medicalInsurance
            }}
            initialValues={getStepPendencyValues(
              DoctorRegistrationSteps.ATTENDANCES
            )}
            updateStepStatus={updateStepStatus}
          />
        )
      },
      {
        label: 'Imunização',
        hide: isDirector,
        componentChildren: (
          <Immunizations
            immunizations={doctorDetails?.immunization}
            initialValues={getStepPendencyValues(
              DoctorRegistrationSteps.IMUNNIZATION
            )}
            updateStepStatus={updateStepStatus}
          />
        )
      },
      {
        label: 'Documentos e anexos',
        componentChildren: (
          <Documents
            documents={doctorDetails?.doctorDocuments ?? []}
            identificationsData={{
              rg: doctorDetails?.rg,
              cpf: doctorDetails?.cpf
            }}
            doctor_id={state?.doctor_id}
            initialValues={getStepPendencyValues(
              DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS
            )}
            updateStepStatus={updateStepStatus}
            isDirector={isDirector}
          />
        )
      },
      {
        label: 'Validação da fonte',
        componentChildren: (
          <SourceValidation
            initialValues={getStepPendencyValues(
              DoctorRegistrationSteps.SOURCE_VALIDATION
            )}
            isDirector={isDirector}
            doctorRegister={doctorDetails?.doctorRegister}
            documentValidations={
              doctorDetails?.doctorRegister?.documentValidations ?? []
            }
            updateStepStatus={updateStepStatus}
            graduation={doctorDetails?.academicFormation || ''}
            primarySpeciality={getMainSpeciality(doctorDetails)?.name || ''}
            secondarySpecialities={getParsedSecondarySpecialities()}
            onPageLoad={fetchDoctorWithRecommendation}
          />
        )
      }
    ]

    const loadHospitalsUnits = async () => {
      await currentHospital.load()
      const hospitals = currentHospital.hospitalList
      const units = doctorDetails?.getDoctorRecommendation?.units.split(',')
      if (units) {
        const foundUnits = units?.map((unit: string) => {
          return hospitals.find(
            (hospital) => hospital.hospital_id === Number(unit)
          )
        }) as Hospital[]
        setHospitalUnits(foundUnits ?? [])
      }
    }

    useEffect(() => {
      fetchDoctorWithRecommendation()
    }, [state?.doctor_id])

    useEffect(() => {
      loadHospitalsUnits()
    }, [doctorDetails])

    useEffect(() => {
      fetchDoctorWithRecommendation()
      loadHospitalsUnits()
    }, [toggleInfo])

    const canCreatePendency = () => {
      return (
        newRegistrationDetailsContext.pendencies.length > 0 &&
        !newRegistrationDetailsContext.steps?.some(
          (step) => step.status === DoctorRegisterStepStatus.UNDER_ANALYSIS
        ) &&
        newRegistrationDetailsContext.register?.status !==
          DoctorRegisterStatus.PENDENT
      )
    }

    const canCreateReview = () => {
      const hasReview = newRegistrationDetailsContext.toReview.length > 0

      const hasApprovedByDoctorRegister = newRegistrationDetailsContext
        .getDirectorSteps()
        .some((step) => {
          return (
            step.status === DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER
          )
        })

      const isReviewedByDirector =
        newRegistrationDetailsContext.register?.status ===
        DoctorRegisterStatus.REVIEWED_BY_DIRECTOR

      return hasReview && !hasApprovedByDoctorRegister && !isReviewedByDirector
    }

    async function updateStepStatus(step: any) {
      const stepKey = Object.keys(step) as Array<DoctorRegistrationSteps>

      for (const key of stepKey) {
        const actualStep = newRegistrationDetailsContext.getStep(key)

        if (!actualStep || !actualStep.status) {
          return
        }

        const newValues = step[key]

        const initialValue: {
          description?: string
          status: DoctorRegisterStepStatus
        } = {
          description: actualStep.getActivePendency?.text,
          status: actualStep.status
        }

        const criteria =
          actualStep.status ===
            DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER ||
          actualStep.status === DoctorRegisterStepStatus.APPROVED_DIRECTOR
            ? !isEqual(initialValue.status, newValues.status)
            : !isEqual(initialValue, newValues)

        if (newValues.status === DoctorRegisterStepStatus.PENDENT && criteria) {
          const pendency = await doctorService.createDoctorRegisterPendency({
            text: newValues.description || '',
            doctor_register_step_id: Number(actualStep?.doctor_register_step_id)
          })
          actualStep.getActivePendency = pendency
        } else if (
          (newValues.status ===
            DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER ||
            newValues.status === DoctorRegisterStepStatus.APPROVED_DIRECTOR) &&
          criteria
        ) {
          await doctorRegisterStepService.updateDoctorRegisterStep({
            doctor_register_step_id: Number(
              actualStep?.doctor_register_step_id
            ),
            status: newValues.status
          })
        } else if (
          newValues.status === DoctorRegisterStepStatus.REVIEW &&
          criteria
        ) {
          await doctorRegister.createDoctorRegisterReview({
            doctor_register_step_id: Number(
              actualStep?.doctor_register_step_id
            ),
            text: newValues.director_review
          })
        }

        newRegistrationDetailsContext.updateSteps({
          ...actualStep,
          status: newValues.status
        })
      }
    }

    const sendToDirector = async () => {
      try {
        if (doctorDetails?.doctorRegister?.doctor_register_id) {
          await doctorRegister.sendDoctorRegisterToDirector({
            doctor_register_id: doctorDetails.doctorRegister.doctor_register_id
          })
        }
        setShowSuccessModal(true)
      } catch (e) {
        toast.error(
          'Ocorreu um erro ao tentar enviar o cadastro para o diretor'
        )
      }
    }

    const sendToReview = async () => {
      if (!newRegistrationDetailsContext?.register?.doctor_register_id) return

      try {
        await doctorService.updateDoctorRegisterStatus({
          doctor_register_id:
            newRegistrationDetailsContext.register.doctor_register_id,
          status: DoctorRegisterStatus.REVIEWED_BY_DIRECTOR
        })

        newRegistrationDetailsContext.addRegister(
          new DoctorRegister({
            ...newRegistrationDetailsContext.register,
            status: DoctorRegisterStatus.REVIEWED_BY_DIRECTOR
          })
        )
        toast.success('Cadastro enviado para revisão')
        setShowReviewModal(false)
      } catch {
        toast.error(
          'Ocorreu um erro ao tentar enviar o cadastro para a revisão'
        )
      }
    }

    const approveRegister = async () => {
      if (!newRegistrationDetailsContext?.register?.doctor_register_id) return

      try {
        await doctorService.updateDoctorRegisterStatus({
          doctor_register_id:
            newRegistrationDetailsContext.register.doctor_register_id,
          status: DoctorRegisterStatus.APPROVED_BY_DIRECTOR_WITH_PENDENCIES
        })

        newRegistrationDetailsContext.addRegister(
          new DoctorRegister({
            ...newRegistrationDetailsContext.register,
            status: DoctorRegisterStatus.APPROVED_BY_DIRECTOR_WITH_PENDENCIES
          })
        )
        toast.success('Cadastro médico aprovado!')
        setShowApprovedModal(false)
      } catch {
        toast.error('Ocorreu um erro ao tentar aprovar o cadastro médico')
      }
    }

    const reproveRegister = async () => {
      if (!newRegistrationDetailsContext?.register?.doctor_register_id) return

      try {
        await doctorService.updateDoctorRegisterStatus({
          doctor_register_id:
            newRegistrationDetailsContext.register.doctor_register_id,
          status: DoctorRegisterStatus.REGISTER_REPROVED_BY_DIRECTOR
        })

        newRegistrationDetailsContext.addRegister(
          new DoctorRegister({
            ...newRegistrationDetailsContext.register,
            status: DoctorRegisterStatus.REGISTER_REPROVED_BY_DIRECTOR
          })
        )
        toast.success('Cadastro médico reprovado!')
        setShowReprovedModal(false)
      } catch {
        toast.error('Ocorreu um erro ao tentar reprovar o cadastro médico')
      }
    }

    const canReprove = () => {
      if (!newRegistrationDetailsContext?.register?.doctor_register_id)
        return false

      const status = newRegistrationDetailsContext.register.status

      const approvedOrReprovedStatus = [
        DoctorRegisterStatus.APPROVED_BY_DIRECTOR_WITH_PENDENCIES,
        DoctorRegisterStatus.REGISTER_REPROVED_BY_DIRECTOR,
        DoctorRegisterStatus.REGISTER_CONCLUDED
      ]

      return !approvedOrReprovedStatus.includes(status)
    }

    const getDoctorName = () => {
      if (!doctorDetails?.name) return ''
      const firstName = doctorDetails.name.split(' ').shift()
      const lastName = doctorDetails.name.split(' ').pop()

      if (firstName === lastName) {
        return `${firstName}`
      }

      return `${firstName} ${lastName}`
    }

    return (
      <Container>
        <S.Header>
          <ActualPage
            data-testid="btn-go-back"
            text="Voltar"
            onClick={history.goBack}
          />
          <S.HeaderButtons>
            <Button
              data-testid="btn-generate-pendency"
              variant={isDirector ? 'outlined' : undefined}
              onClick={() =>
                isDirector
                  ? setShowReviewModal(true)
                  : setShowPendencyModal(true)
              }
              disabled={isDirector ? !canCreateReview() : !canCreatePendency()}
            >
              {isDirector ? 'Solicitar revisão' : 'Gerar pendência'}
            </Button>
            {isDirector && (
              <Button
                backgroundColor={'alert'}
                data-testid="btn-reprove-register"
                onClick={() => setShowReprovedModal(true)}
                disabled={!canReprove()}
              >
                Reprovar
              </Button>
            )}
            <Button
              data-testid="btn-send-to-director"
              disabled={
                !isDirector
                  ? !newRegistrationDetailsContext.canSendToDirector
                  : !newRegistrationDetailsContext.canApprove
              }
              type="button"
              onClick={() =>
                isDirector
                  ? setShowApprovedModal(true)
                  : setSendToDirectorModal(true)
              }
            >
              {isDirector ? 'Aprovar' : 'Enviar para o diretor'}
            </Button>
          </S.HeaderButtons>
        </S.Header>
        <S.Wrapper>
          <S.SidePanel data-testid="overview-panel">
            <OverviewCard
              doctor={{
                name: getDoctorName(),
                phone: doctorDetails?.phone ?? '',
                email: doctorDetails?.email ?? ''
              }}
              complement={{
                academicFormation: getAcademicFormationDescription(),
                crm: getMainCouncilRegister()?.crm ?? '',
                crmUf: getMainCouncilRegister()?.crmUf ?? '',
                specialty: getMainSpeciality(doctorDetails)?.name ?? '',
                isResident: !!doctorDetails?.materDeiStudent,
                modality: doctorDetails?.modality,
                sendDocumentDate:
                  doctorDetails?.doctorRegister?.sentToHospitalAt?.toLocaleDateString(),
                indicationDate:
                  doctorDetails?.getDoctorRecommendation?.createdAt?.toLocaleDateString(),
                registerInMvDate:
                  doctorDetails?.createdInMvAt?.toLocaleDateString()
              }}
              units={getFormattedUnits()}
              actingSectors={getActingSectorsDescriptions()}
            />
          </S.SidePanel>
          <S.Content data-testid="tabs-panel">
            {doctorDetails && hospitalUnits.length > 0 ? (
              <ScrollableTabs
                tabItems={[...tabItems.filter((item) => !item.hide)]}
              />
            ) : (
              <></>
            )}
          </S.Content>
        </S.Wrapper>
        <CreateRegisterPendencyModal
          show={showPendencyModal}
          doctor_register_id={Number(
            doctorDetails?.doctorRegister?.doctor_register_id
          )}
          onClose={() => setShowPendencyModal(false)}
        />
        <ConfirmationModal
          data-testid="modal-confirm-generate-review"
          show={showReviewModal}
          style={{ width: '554px' }}
          preventAutomateClose
        >
          <img
            style={{ height: '165px' }}
            src={DoctorReview}
            alt={'Ilustração'}
          />
          <Heading size="large">Deseja enviar o cadastro para revisão?</Heading>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button
              data-testid="btn-cancel-confirm-generate-pendency"
              variant="outlined"
              type="button"
              onClick={() => setShowReviewModal(false)}
              style={{ width: '48%' }}
            >
              Cancelar
            </Button>
            <Button
              data-testid="btn-confirm-generate-pendency"
              type="button"
              onClick={sendToReview}
              style={{ width: '48%' }}
            >
              Prosseguir
            </Button>
          </div>
        </ConfirmationModal>
        <ConfirmationModal
          data-testid="modal-confirm-approve-register"
          show={showApprovedModal}
          style={{ width: '554px', height: '40%' }}
          preventAutomateClose
        >
          <img
            style={{ height: '165px' }}
            src={DoctorConfirmation}
            alt={'Ilustração'}
          />
          <Heading size="large">Deseja aprovar o cadastro?</Heading>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button
              data-testid="btn-cancel-confirm-generate-pendency"
              variant="outlined"
              type="button"
              onClick={() => setShowApprovedModal(false)}
              style={{ width: '48%' }}
            >
              Cancelar
            </Button>
            <Button
              data-testid="btn-confirm-generate-pendency"
              type="button"
              onClick={approveRegister}
              style={{ width: '48%' }}
            >
              Prosseguir
            </Button>
          </div>
        </ConfirmationModal>
        <ConfirmationModal
          data-testid="modal-confirm-reprove-register"
          show={showReprovedModal}
          style={{ width: '554px', height: '40%' }}
          preventAutomateClose
        >
          <img
            style={{ height: '165px' }}
            src={ReproveRegisterImage}
            alt={'Ilustração'}
          />
          <Heading size="large">Deseja reprovar o cadastro?</Heading>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button
              data-testid="btn-cancel-confirm-generate-pendency"
              variant="outlined"
              type="button"
              onClick={() => setShowReprovedModal(false)}
              style={{ width: '48%' }}
            >
              Cancelar
            </Button>
            <Button
              data-testid="btn-confirm-generate-pendency"
              type="button"
              onClick={reproveRegister}
              style={{ width: '48%' }}
            >
              Prosseguir
            </Button>
          </div>
        </ConfirmationModal>
        <ConfirmationModal
          data-testid="modal-confirm-generate-pendency"
          show={sendToDirectorModal}
          style={{ width: '554px', height: '40%' }}
          preventAutomateClose
        >
          <img
            style={{ height: '165px' }}
            src={DoctorConfirmation}
            alt={'Ilustração'}
          />
          <Heading size="large">
            Deseja enviar o cadastro para o Diretor Médico?
          </Heading>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button
              data-testid="btn-cancel-confirm-generate-pendency"
              variant="outlined"
              type="button"
              onClick={() => setSendToDirectorModal(false)}
              style={{ width: '48%' }}
            >
              Cancelar
            </Button>
            <Button
              data-testid="btn-confirm-generate-pendency"
              type="button"
              onClick={sendToDirector}
              style={{ width: '48%' }}
            >
              Prosseguir
            </Button>
          </div>
        </ConfirmationModal>
        <SuccessModal
          data-testid="modal-success-generate-pendency"
          show={showSuccessModal}
          style={{ width: '554px', height: '40%' }}
          preventAutomateClose
        >
          <img
            style={{ height: '165px' }}
            src={SuccessRegisterPendency}
            alt={'Ilustração'}
          />
          <Heading size="large">Cadastro enviado com sucesso</Heading>
          <Button
            data-testid="btn-success-generate-pendency"
            style={{ marginTop: '2rem' }}
            type="button"
            onClick={() => history.goBack()}
          >
            Voltar a novos cadastros
          </Button>
        </SuccessModal>
      </Container>
    )
  })
)

export function generateNewRegistrationDetailsValidationSchema(
  ...steps: DoctorRegistrationSteps[]
) {
  const { isDirector } = newRegistrationDetailsContext
  const schema = {} as Record<DoctorRegistrationSteps, any>
  steps.forEach((step) => {
    schema[step] = yup.object().shape({
      status: yup
        .string()
        .test('is-valid-status', 'Status inválido', (value) =>
          isDirector
            ? value === DoctorRegisterStepStatus.REVIEW ||
              value === DoctorRegisterStepStatus.APPROVED_DIRECTOR
            : value === DoctorRegisterStepStatus.PENDENT ||
              value === DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER ||
              value === DoctorRegisterStepStatus.APPROVED_DIRECTOR
        ),
      description: yup.string().when('status', {
        is: DoctorRegisterStepStatus.PENDENT,
        then: yup.string().required('Campo obrigatório')
      }),
      director_review: yup.string().when('status', {
        is: DoctorRegisterStepStatus.REVIEW,
        then: yup.string().required('Campo obrigatório')
      })
    })
  })

  return yup.object().shape(schema)
}

export function disableObservation(step: DoctorRegistrationSteps) {
  return newRegistrationDetailsContext.getStep(step)?.status === 'PENDENT'
}

export default NewRegistrationDetailsLayout

type PendencyInitialValues = Record<
  DoctorRegistrationSteps,
  {
    description: string
    director_review?: string
    status: DoctorRegisterStepStatus
  }
>
